/**
 * ProposalRoiCalculator — "See the Impact" section inside the New Business deck.
 * SAVE MONEY / MAKE MONEY tabs, a log-scale annual-visits slider, package chips
 * (typical visit = one of the proposal's bundles) and a tweened hero £ figure.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { PiggyBank, TrendingUp, Sparkles, Info } from "lucide-react";
import { normaliseRoi, bundleBaseCost, computeRoi, fmtGBP, fmtMoney, snapVisits, sqftNote, roiModes, EXTRA_MODE_LABELS } from "./roiMath";

const easeOut = (p) => 1 - Math.pow(1 - p, 3);

function useTweened(target, ms = 480) {
  const [value, setValue] = useState(target);
  const live = useRef(target);
  useEffect(() => {
    const from = live.current;
    if (from === target) return undefined;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / ms);
      const v = from + (target - from) * easeOut(p);
      live.current = v;
      setValue(v);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return value;
}

const SLIDER_STEPS = 1000;
const toVisits = (pos, max) => snapVisits(Math.exp((pos / SLIDER_STEPS) * Math.log(max)), max);
const toPos = (visits, max) => Math.round((Math.log(Math.max(1, visits)) / Math.log(max)) * SLIDER_STEPS);

function Row({ label, detail, value, op, strong, accent, editable, testId }) {
  return (
    <div className={`nbp-roi-row ${strong ? "is-strong" : ""} ${accent ? "is-accent" : ""}`} data-testid={testId}>
      <span className="nbp-roi-op">{op || ""}</span>
      <span className="nbp-roi-row-label">
        {label}
        {detail && <small>{detail}</small>}
      </span>
      <span className="nbp-roi-row-value">{editable || value}</span>
    </div>
  );
}

export default function ProposalRoiCalculator({ proposal }) {
  const roi = useMemo(() => normaliseRoi(proposal.roi), [proposal.roi]);
  const bundles = proposal.bundles || [];
  const max = Math.max(10, Math.min(10000, Number(roi.visits_max) || 10000));

  const modes = roiModes(roi);
  const [tab, setTab] = useState(modes[0] || "save");
  const [visits, setVisits] = useState(() => snapVisits(roi.annual_visits_default || 100, max));
  const [visitsText, setVisitsText] = useState(String(snapVisits(roi.annual_visits_default || 100, max)));
  const [bundleIdx, setBundleIdx] = useState(() => {
    const i = Number(roi.base_bundle_index);
    return Number.isInteger(i) && i >= 0 && i < bundles.length ? i : 0;
  });
  const [currentCost, setCurrentCost] = useState(Number(roi.current_cost_per_visit) || 0);

  const baseCost = bundles.length ? bundleBaseCost(bundles[bundleIdx]) : Number(roi.nichecom_cost_per_visit) || 0;
  const calc = useMemo(() => computeRoi({ roi, visits, baseCost, currentCost }), [roi, visits, baseCost, currentCost]);

  const heroTarget = tab === "save" ? calc.save.saving : calc.make.annualMargin;
  const hero = useTweened(heroTarget);
  const heroNegative = tab === "save" && calc.save.saving < 0;

  const applyVisits = (v) => {
    const snapped = snapVisits(v, max);
    setVisits(snapped);
    setVisitsText(String(snapped));
  };

  const ticks = [1, 10, 100, 1000, 10000].filter((t) => t <= max);
  const pct = (n) => `${(n * 100).toFixed(0)}%`;

  return (
    <section className="nbp-roi" id="see-the-impact" data-testid="nbp-roi">
      <div className="nbp-section-head">
        <span className="nbp-eyebrow-dark">See the impact</span>
        <h2 className="nbp-h2">Move the slider to your annual visit volume</h2>
        <p className="nbp-section-sub">
          {modes.length === 2
            ? "See exactly what NicheCom could save you — or what you could make by passing the service on to your vendors."
            : modes[0] === "make"
              ? "See what you could make by passing the service on to your vendors."
              : "See exactly what NicheCom could save you every year."}
        </p>
      </div>

      {modes.length > 1 ? (
        <div className="nbp-roi-tabs" role="tablist" data-testid="nbp-roi-tabs">
          <button
            role="tab"
            aria-selected={tab === "save"}
            className={`nbp-roi-tab ${tab === "save" ? "is-active" : ""}`}
            onClick={() => setTab("save")}
            data-testid="nbp-roi-tab-save"
          >
            <PiggyBank size={16} /> Save money
          </button>
          <button
            role="tab"
            aria-selected={tab === "make"}
            className={`nbp-roi-tab ${tab === "make" ? "is-active" : ""}`}
            onClick={() => setTab("make")}
            data-testid="nbp-roi-tab-make"
          >
            <TrendingUp size={16} /> Make money
          </button>
        </div>
      ) : (
        <div className="nbp-roi-tabs nbp-roi-tabs-single" data-testid="nbp-roi-single-mode">
          <span className="nbp-roi-tab is-active is-static">
            {tab === "make" ? <><TrendingUp size={16} /> Make money</> : <><PiggyBank size={16} /> Save money</>}
          </span>
        </div>
      )}

      <div className="nbp-roi-panel">
        <div className={`nbp-roi-hero ${heroNegative ? "is-negative" : ""}`} data-testid="nbp-roi-hero">
          <span className="nbp-roi-hero-eyebrow">
            {tab === "save" ? (heroNegative ? "At this volume NicheCom would cost" : "You could save") : "You could generate"}
          </span>
          <span className="nbp-roi-hero-figure" data-testid="nbp-roi-hero-figure">
            {fmtGBP(Math.abs(hero))}
          </span>
          <span className="nbp-roi-hero-tail">
            {tab === "save"
              ? heroNegative ? "more per year than today" : "per year with NicheCom"
              : "additional margin per year"}
          </span>
          <div className="nbp-roi-hero-meta">
            <span className="nbp-roi-pill" data-testid="nbp-roi-visits-pill">{visits.toLocaleString("en-GB")} visits / year</span>
            {bundles.length > 0 && (
              <span className="nbp-roi-pill">{bundles[bundleIdx]?.name} package · {fmtMoney(baseCost)} per visit</span>
            )}
            {tab === "make" && <span className="nbp-roi-pill">+{pct(calc.uplift)} vendor uplift</span>}
          </div>
        </div>

        <div className="nbp-roi-body">
          <div className="nbp-roi-controls">
            <div className="nbp-roi-slider-head">
              <label htmlFor="nbp-roi-slider">Annual visits</label>
              <span className="nbp-roi-visits-input">
                <input
                  type="number"
                  min={1}
                  max={max}
                  value={visitsText}
                  onChange={(e) => {
                    setVisitsText(e.target.value);
                    const n = Number(e.target.value);
                    if (n >= 1) setVisits(Math.min(Math.max(Math.round(n), 1), max));
                  }}
                  onBlur={() => applyVisits(Number(visitsText) || 1)}
                  data-testid="nbp-roi-visits-input"
                />
                <span>visits</span>
              </span>
            </div>
            <input
              id="nbp-roi-slider"
              className="nbp-roi-slider"
              type="range"
              min={0}
              max={SLIDER_STEPS}
              value={toPos(visits, max)}
              onChange={(e) => applyVisits(toVisits(Number(e.target.value), max))}
              style={{ "--pos": `${(toPos(visits, max) / SLIDER_STEPS) * 100}%` }}
              aria-valuemin={1}
              aria-valuemax={max}
              aria-valuenow={visits}
              aria-label="Annual visits"
              data-testid="nbp-roi-slider"
            />
            <div className="nbp-roi-ticks" aria-hidden="true">
              {ticks.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`nbp-roi-tick ${visits === t ? "is-active" : ""}`}
                  style={{ left: `${(toPos(t, max) / SLIDER_STEPS) * 100}%` }}
                  onClick={() => applyVisits(t)}
                  tabIndex={-1}
                >
                  {t >= 1000 ? `${t / 1000}k` : t}
                </button>
              ))}
            </div>

            {bundles.length > 1 && (
              <div className="nbp-roi-chips" data-testid="nbp-roi-package-chips">
                <span className="nbp-roi-chips-label">Typical visit</span>
                {bundles.map((b, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`nbp-roi-chip ${i === bundleIdx ? "is-active" : ""}`}
                    onClick={() => setBundleIdx(i)}
                    data-testid={`nbp-roi-chip-${i}`}
                  >
                    {b.name} <em>{fmtMoney(bundleBaseCost(b))}</em>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="nbp-roi-breakdown" key={tab} data-testid={`nbp-roi-breakdown-${tab}`}>
            {tab === "save" ? (
              <>
                <Row
                  label="Current cost per visit"
                  detail="What you pay today — type your real figure"
                  testId="nbp-roi-row-current"
                  editable={
                    <span className="nbp-roi-edit">
                      <span>£</span>
                      <input
                        type="number"
                        min={0}
                        step="1"
                        value={currentCost}
                        onChange={(e) => setCurrentCost(Math.max(0, Number(e.target.value) || 0))}
                        aria-label="Current cost per visit"
                        data-testid="nbp-roi-current-cost"
                      />
                    </span>
                  }
                />
                <Row op="×" label="Annual visits" value={visits.toLocaleString("en-GB")} />
                <Row op="=" label="Current annual spend" value={fmtGBP(calc.save.currentAnnual)} strong testId="nbp-roi-current-annual" />
                <div className="nbp-roi-divider" />
                <Row
                  label="NicheCom cost per visit"
                  detail={
                    calc.agencyExtras > 0
                      ? `${bundles[bundleIdx]?.name || "Package"} ${fmtMoney(calc.base)} + extras ${fmtMoney(calc.agencyExtras)}`
                      : bundles[bundleIdx]?.name ? `${bundles[bundleIdx].name} package` : undefined
                  }
                  value={fmtMoney(calc.save.nichecomPerVisit)}
                  testId="nbp-roi-row-nichecom"
                />
                <Row op="×" label="Annual visits" value={visits.toLocaleString("en-GB")} />
                <Row op="=" label="NicheCom annual spend" value={fmtGBP(calc.save.nichecomAnnual)} strong testId="nbp-roi-nichecom-annual" />
                {calc.save.offsetAnnual > 0 && (
                  <Row
                    op="+"
                    label={calc.save.offsetLabel}
                    detail={`${fmtMoney(calc.save.offsetPerVisit)} per visit at +${pct(calc.uplift)}`}
                    value={fmtGBP(calc.save.offsetAnnual)}
                    testId="nbp-roi-offset"
                  />
                )}
                <Row
                  label={heroNegative ? "Annual difference" : "Annual saving"}
                  value={`${heroNegative ? "−" : ""}${fmtGBP(Math.abs(calc.save.saving))}`}
                  strong
                  accent
                  testId="nbp-roi-saving"
                />
              </>
            ) : (
              <>
                <Row
                  label="NicheCom cost per visit"
                  detail={
                    calc.extras.some((e) => e.mode !== "included")
                      ? `${bundles[bundleIdx]?.name || "Package"} ${fmtMoney(calc.base)} + chargeable extras ${fmtMoney(calc.chargeablePerVisit - calc.base)}`
                      : bundles[bundleIdx]?.name ? `${bundles[bundleIdx].name} package` : undefined
                  }
                  value={fmtMoney(calc.make.costPerVisit)}
                  testId="nbp-roi-row-cost"
                />
                <Row op="→" label={`Vendor charge (+${pct(calc.uplift)})`} value={fmtMoney(calc.make.vendorChargePerVisit)} testId="nbp-roi-row-vendor" />
                <Row op="=" label="Margin per visit" value={fmtMoney(calc.make.marginPerVisit)} strong testId="nbp-roi-row-margin" />
                <div className="nbp-roi-divider" />
                <Row op="×" label="Annual visits" value={visits.toLocaleString("en-GB")} />
                <Row op="=" label="Annual additional margin" value={fmtGBP(calc.make.annualMargin)} strong accent testId="nbp-roi-margin-annual" />
              </>
            )}
          </div>
        </div>

        {(calc.extras.length > 0 || sqftNote(roi)) && (
          <div className="nbp-roi-extras" data-testid="nbp-roi-extras">
            <Info size={13} />
            {sqftNote(roi) && (
              <span className="nbp-roi-extra is-note" data-testid="nbp-roi-sqft-note"><strong>Large properties</strong> {sqftNote(roi).replace("Properties over", "over")}</span>
            )}
            {calc.extras.map((e) => (
              <span key={e.key} className={`nbp-roi-extra is-${e.mode}`} title={e.detail}>
                <strong>{e.label}</strong> {EXTRA_MODE_LABELS[e.mode] || e.mode}
                {e.mode !== "included" && <em>{fmtMoney(e.perVisit)} / visit</em>}
              </span>
            ))}
          </div>
        )}
      </div>

      <p className="nbp-roi-foot">
        <Sparkles size={12} /> Figures are illustrative, based on the pricing in this proposal and the assumptions shown.
      </p>
    </section>
  );
}
